import { useCallback, useEffect, useRef, useState } from "react";
import Select, {
  InputActionMeta,
  MultiValue,
  StylesConfig,
  components,
  MenuProps,
  OptionProps,
  MultiValueRemoveProps,
} from "react-select";
import { PolicyAxiosInstance } from "../../Network/PolicyAxiosInstance";
import { Option } from "../../interface/Option";

interface PlanPolicySelectorProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];
  value?: Option[];
  qaIdPrefix?: string;
}

const DEFAULT_QA_PREFIX = "plan-policy-select";
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;
const PAGE_LIMIT = 100;

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildQaId = (prefix: string, suffix: string | number) =>
  `${qaSafe(prefix)}-${qaSafe(suffix)}`;

const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = buildQaId(prefix, suffix);
  return {
    id,
    "data-testid": id,
    "data-qa": id,
  };
};

// Cache by search query so reopening the Edit dialog does not repeat
// the exact same Policy request unnecessarily.
const policyCache = new Map<string, Option[]>();
const policyInFlight = new Map<string, Promise<Option[]>>();

const extractRows = (response: any): any[] => {
  const raw =
    response?.data?.result ??
    response?.data?.data ??
    response?.data?.policies ??
    [];

  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.result)) return raw.result;

  return [];
};

// Inactive belongs to the linked Policy, not to the Insurance Plan.
const policyInactive = (option: any): boolean => {
  if (!option || typeof option !== "object") return false;
  if (option.__inactive === true || option.isInactive === true || option.is_deleted === true) return true;
  const flag = option.is_active ?? option.Is_active ?? option.active ?? option.status ?? option.Status;
  return flag === false || flag === 0 || flag === "0" ||
    ["false", "inactive", "disabled"].includes(String(flag ?? "").trim().toLowerCase());
};

const policyKey = (option: any) => String(option?.policyId ?? option?.Policy_id ?? option?.value ?? "").trim();

const mergeSelectedStatus = (selected: Option[], catalogue: Option[]): Option[] => {
  const byId = new Map(catalogue.map((option) => [policyKey(option), option]));
  return selected.map((option) => {
    const match = byId.get(policyKey(option));
    if (!match) return option;
    // Keep the SQL DB ID and the parent-provided inactive state.
    return { ...match, ...option,
      policyDbId: (option as any).policyDbId ?? (match as any).policyDbId,
      __inactive: policyInactive(option) || policyInactive(match),
    } as Option;
  });
};

const formatPolicyRows = (rows: any[]): Option[] =>
  rows
    .map((item: any) => {
      const dbId =
        item?.id ??
        item?.policy_db_id ??
        item?.policyDbId;

      const policyId = String(
        item?.Policy_id ??
          item?.policy_id ??
          item?.policyId ??
          ""
      ).trim();

      const policyName = String(
        item?.Policy_name ??
          item?.policy_name ??
          item?.policyName ??
          item?.name ??
          ""
      ).trim();

      if (!policyId || !policyName) {
        console.warn("Invalid Policy item:", item);
        return null;
      }

      return {
        // IMPORTANT:
        // InsurancePlan.tsx currently builds its payload as:
        // Policy_id: String(option.value)
        //
        // Therefore value must stay the BUSINESS Policy_id here.
        // Do NOT change this selector value to the SQL DB id.
        value: policyId,
        label: `${policyId} - ${policyName}`,

        // Extra metadata is safe and useful for QA/debugging.
        policyDbId:
          dbId !== null && dbId !== undefined
            ? Number(dbId)
            : undefined,
        policyId,
        policyName,
        __inactive: policyInactive(item),
        is_active: item?.is_active ?? item?.isActive,
        status: item?.status ?? item?.Status,
      } as Option;
    })
    .filter((item: Option | null): item is Option => item !== null);

const fetchPolicies = async (query: string): Promise<Option[]> => {
  const normalizedQuery = query.trim().toLowerCase();

  const cached = policyCache.get(normalizedQuery);
  if (cached) return cached;

  const existingRequest = policyInFlight.get(normalizedQuery);
  if (existingRequest) return existingRequest;

  // CURRENT backend endpoint:
  // GET /Policy/getPolicy
  const request = PolicyAxiosInstance.get("getPolicy", {
    params: {
      page: 1,
      limit: PAGE_LIMIT,
      name: query.trim() || undefined,
      active: "true",
      is_deleted: "false",
    },
  })
    .then((response) => {
      const formatted = formatPolicyRows(extractRows(response));
      policyCache.set(normalizedQuery, formatted);
      return formatted;
    })
    .finally(() => {
      policyInFlight.delete(normalizedQuery);
    });

  policyInFlight.set(normalizedQuery, request);
  return request;
};

const PlanPolicySelector = ({
  onChange,
  clear,
  defaultValue,
  value,
  qaIdPrefix = DEFAULT_QA_PREFIX,
}: PlanPolicySelectorProps) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(
    value || defaultValue || []
  );
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");

  const requestSequence = useRef(0);

  const selectQaId = buildQaId(qaIdPrefix, "select");
  const inputQaId = buildQaId(qaIdPrefix, "input");

  const loadOptions = useCallback(async (searchText: string) => {
    const currentRequest = ++requestSequence.current;
    setLoading(true);

    try {
      const formatted = await fetchPolicies(searchText);

      // Ignore stale requests when the user types quickly.
      if (currentRequest === requestSequence.current) {
        setOptions(formatted.filter((option) => !policyInactive(option)));
      }
    } catch (error) {
      console.error("Error loading Policies:", error);

      if (currentRequest === requestSequence.current) {
        setOptions([]);
      }
    } finally {
      if (currentRequest === requestSequence.current) {
        setLoading(false);
      }
    }
  }, []);

  // Load the first active Policy page when the selector mounts.
  useEffect(() => {
    void loadOptions("");
  }, [loadOptions]);

  // Search through the backend with getPolicy?name=...
  useEffect(() => {
    const query = inputValue.trim();

    if (!query) {
      const timer = window.setTimeout(() => {
        void loadOptions("");
      }, SEARCH_DEBOUNCE_MS);

      return () => window.clearTimeout(timer);
    }

    if (query.length < MIN_SEARCH_LENGTH) {
      requestSequence.current += 1;
      setOptions([]);
      setLoading(false);
      return;
    }

    const timer = window.setTimeout(() => {
      void loadOptions(query);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [inputValue, loadOptions]);

  const handleChange = (selected: MultiValue<Option>) => {
    const proposed = [...selected];
    // react-select may remove via Backspace / clear-value even without the × button.
    const protectedOptions = selectedOptions.filter((option) =>
      policyInactive(option) && !proposed.some((candidate) => policyKey(candidate) === policyKey(option))
    );
    const next = mergeSelectedStatus([...proposed, ...protectedOptions], options);
    setSelectedOptions(next);
    onChange(next);
  };

  useEffect(() => {
    if (clear) {
      setSelectedOptions([]);
    }
  }, [clear]);

  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedOptions(mergeSelectedStatus(value, options));
    }
  }, [value]);

  useEffect(() => {
    if (value === undefined && Array.isArray(defaultValue)) {
      setSelectedOptions(mergeSelectedStatus(defaultValue, options));
    }
  }, [defaultValue, value]);

  const handleInputChange = (
    nextValue: string,
    actionMeta: InputActionMeta
  ): string => {
    if (actionMeta.action === "input-change") {
      setInputValue(nextValue);
    }

    return nextValue;
  };

  // IMPORTANT:
  // Keep QA attributes on the actual react-select elements.
  // Do not wrap Menu/Option with extra divs because that can interfere
  // with mouse interaction inside dialogs.
  const Menu = (props: MenuProps<Option, true>) => {
    const qa = qaAttrs(qaIdPrefix, "menu");

    return (
      <components.Menu
        {...props}
        innerProps={{
          ...(props.innerProps || {}),
          ...qa,
        }}
      />
    );
  };

  const OptionComponent = (props: OptionProps<Option, true>) => {
    const qa = qaAttrs(
      qaIdPrefix,
      `option-${qaSafe(String(props.data.value))}`
    );

    return (
      <components.Option
        {...props}
        innerProps={{
          ...(props.innerProps || {}),
          ...qa,
          "data-option-label": props.data.label,
          "data-policy-id": String(props.data.value),
        } as any}
      />
    );
  };

  const MultiValueRemove = (props: MultiValueRemoveProps<Option, true>) =>
    policyInactive(props.data) ? null : <components.MultiValueRemove {...props} />;

  const customStyles: StylesConfig<Option, true> = {
    control: (styles) => ({
      ...styles,
      minHeight: "40px",
      fontSize: "14px",
      borderRadius: "8px",
      cursor: "pointer",
    }),

    valueContainer: (styles) => ({
      ...styles,
      fontSize: "14px",
    }),

    input: (styles) => ({
      ...styles,
      fontSize: "14px",
    }),

    placeholder: (styles) => ({
      ...styles,
      fontSize: "14px",
      color: "#6B7280",
    }),

    multiValue: (styles, props) => ({
      ...styles,
      backgroundColor: policyInactive(props.data) ? "#e5e7eb" : "#007BFF",
      border: policyInactive(props.data) ? "1px solid #9ca3af" : "none",
      borderRadius: "5px",
    }),

    multiValueLabel: (styles, props) => ({
      ...styles,
      color: policyInactive(props.data) ? "#4b5563" : "#FFFFFF",
      fontWeight: policyInactive(props.data) ? 600 : 400,
      "&::after": policyInactive(props.data) ? {
        content: '" (Inactive)"',
        fontSize: "11px",
      } : {},
    }),

    multiValueRemove: (styles, props) => ({
      ...styles,
      display: policyInactive(props.data) ? "none" : "flex",
      color: "#FFFFFF",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),

    option: (styles, { isSelected, isFocused }) => ({
      ...styles,
      backgroundColor: isSelected
        ? "#007BFF"
        : isFocused
          ? "#E6F0FA"
          : "#FFFFFF",
      color: isSelected ? "#FFFFFF" : "#000000",
      fontSize: "14px",
      cursor: "pointer",
      pointerEvents: "auto",
      ":hover": {
        backgroundColor: "#0056b3",
        color: "#FFFFFF",
      },
    }),

    menu: (styles) => ({
      ...styles,
      zIndex: 999999,
      pointerEvents: "auto",
    }),

    menuList: (styles) => ({
      ...styles,
      maxHeight: 280,
      overflowY: "auto",
      pointerEvents: "auto",
      WebkitOverflowScrolling: "touch",
    }),
  };

  return (
    <div {...qaAttrs(qaIdPrefix, "wrapper")}>
      <Select
        isMulti
        inputId={inputQaId}
        instanceId={selectQaId}
        name={selectQaId}
        classNamePrefix="plan-policy-select"
        className="plan-policy-select"
        aria-label="Plan Policy selector"
        data-testid={selectQaId}
        data-qa={selectQaId}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        onInputChange={handleInputChange}
        placeholder="Type to search or select from list..."
        noOptionsMessage={() =>
          inputValue.trim().length > 0 &&
          inputValue.trim().length < MIN_SEARCH_LENGTH
            ? `Type at least ${MIN_SEARCH_LENGTH} letters to search...`
            : loading
              ? "Loading..."
              : "No policies found"
        }
        styles={customStyles}
        components={{
          Menu,
          Option: OptionComponent,
          MultiValueRemove,
        }}

        // Same stable pattern used by the other working selectors:
        // keep the menu inside the dialog; do not portal it to document.body.
        menuPosition="absolute"
        menuPlacement="auto"
        menuShouldScrollIntoView={false}
        captureMenuScroll={true}
        maxMenuHeight={280}
        closeMenuOnSelect={false}
        blurInputOnSelect={false}
      />
    </div>
  );
};

export default PlanPolicySelector;
